import React, { useState } from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import noop from 'lodash/noop';
import { Link } from 'react-router-dom';
import HomeSvg from '@icons/home.svg';
import Icon, { CloseOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import {
  DASHBOARD_URL,
  TEMP_DASHBOARD_URL,
  DASHBOARD_TYPE,
  HOME_LONG_PATH,
  DATE_SHORTCUTS,
  isAppModule,
} from '@utils/constants';
import UpdateDashboardPopup from '../../UpdateDashboardPopup';
import combineDashboardName from '../../utils/combineDashboardName';
import globalStyles from '../index.less';
import styles from './index.less';
import Confirm from './Confirm';
import getTempDashboardUrl from '../../utils/getTempDashboardUrl';
import useConfirmUtil from './useConfirmOnClose';
import SaveDashboardPopup from '@components/saving/SavingWrapper/SaveDashboardPopup';
import { getPageKeyForSaveService } from '@utils/savedDashboard';
import { useSelector } from 'react-redux';
import { realtimePSDSelector } from '@store/common/selectors';

const REMOVED_SUCCESSFULLY = 'Removed successfully!';

const DashboardTab = ({
  className,
  dashboard,
  dashboards,
  isActive,
  activeId,
  id,
  remove,
  prevDashboard,
  replace,
  isSaved,
  toggleUpdatePopupVisible,
  isSharing,
  customTitle,
  okText,
  cancelText,
  onSave,
  defaultLandingPage,
}) => {
  const pageTypeForSaveService = isSharing ? 'sharing' : isSaved ? 'saved' : null;
  const pageKeyForSaveService = getPageKeyForSaveService(pageTypeForSaveService, id);
  const [isNewSaveDashboardPopupOpen, setIsNewSaveDashboardPopupOpen] = useState(false);

  const pagesSupportingNewSavedDashboard = [
    DASHBOARD_TYPE.TRENDS,
    DASHBOARD_TYPE.ALERT_DIAGNOSTICS,
    DASHBOARD_TYPE.NOC_DRILLDOWN,
    DASHBOARD_TYPE.BROKEN_ASSETS_DRILLDOWN,
    DASHBOARD_TYPE.APP_EXPERIENCE,
    DASHBOARD_TYPE.TRENDS_DRILLDOWN,
  ];

  const realtimePSDFF = useSelector(realtimePSDSelector);

  if (realtimePSDFF) {
    pagesSupportingNewSavedDashboard.push(DASHBOARD_TYPE.NOC);
  }

  const showNewSaveDashboardPopup = pagesSupportingNewSavedDashboard.includes(dashboard.type);

  const handleSave = React.useCallback(
    () => {
      if (showNewSaveDashboardPopup) {
        setIsNewSaveDashboardPopupOpen(true);
      } else {
        if (onSave) {
          onSave();
        }
        toggleUpdatePopupVisible(true, id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  const handleSaveActive = React.useCallback(
    () => {
      if (onSave) {
        onSave();
      }

      if (showNewSaveDashboardPopup) {
        setIsNewSaveDashboardPopupOpen(true);
      } else {
        toggleUpdatePopupVisible(true, activeId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeId],
  );

  const handleDontSave = React.useCallback(
    () => {
      if (isSharing) {
        remove();
        return;
      }
      if (dashboard.type === DASHBOARD_TYPE.COMPARE) {
        remove(id);
        return;
      }

      remove(id, isSaved);
      if (!prevDashboard || get(prevDashboard, 'id', false)) {
        message.success(REMOVED_SUCCESSFULLY);
        replace(HOME_LONG_PATH);
        return;
      }
      if (get(prevDashboard, 'isSaved', false)) {
        replace(
          DASHBOARD_URL[dashboard.type]
            ? `${DASHBOARD_URL[dashboard.type]}${get(prevDashboard, 'id')}`
            : HOME_LONG_PATH,
        );
      } else {
        replace(
          TEMP_DASHBOARD_URL[dashboard.type]
            ? `${TEMP_DASHBOARD_URL[dashboard.type]}${get(prevDashboard, 'id')}`
            : HOME_LONG_PATH,
        );
      }
      message.success(REMOVED_SUCCESSFULLY);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, prevDashboard, isActive, isSaved],
  );

  const removeTab = React.useCallback(
    () => {
      if (dashboard.type === DASHBOARD_TYPE.COMPARE) {
        remove(id);
        return;
      }

      remove(id, isSaved);
      if (isActive) {
        message.success(REMOVED_SUCCESSFULLY);
        replace(HOME_LONG_PATH);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, isActive, isSaved],
  );

  // browser tab warning while closing modified saved dashboard
  useConfirmUtil({ dashboard, isActive });

  const url = getTempDashboardUrl(dashboard, {
    isSharing,
    isSaved,
    id,
  });

  let showPopConfirm = ![
    DASHBOARD_TYPE.VIEWER_MODULE,
    DASHBOARD_TYPE.APP_EXPERIENCE_DRILLDOWN,
    DASHBOARD_TYPE.APP_VIEWER_MODULE,
    // new save dashboard is not working for popup on close -> save
    DASHBOARD_TYPE.TRENDS_DRILLDOWN,
    DASHBOARD_TYPE.BROKEN_ASSETS_DRILLDOWN,
    DASHBOARD_TYPE.NEWAPP_DRILLDOWN,
    DASHBOARD_TYPE.NOC_DRILLDOWN,
    DASHBOARD_TYPE.ALERT_DIAGNOSTICS,
    DASHBOARD_TYPE.EXPERIENCE_ALERT_DIAGNOSTICS,
    DASHBOARD_TYPE.CUSTOM_DASHBOARD_DRILLDOWN,
  ].includes(dashboard.type);

  // ai alert temp dashboard should be closed without confirmation
  // ai alert shared dashboard should show confirmation msg similar to trends
  const isAiAlertTempDashboard =
    dashboard &&
    dashboard.type === DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS &&
    !dashboard.title.includes('Shared');

  const isAppCompareDashboard =
    dashboard && isAppModule && dashboard.type === DASHBOARD_TYPE.COMPARE;

  let popMsg = '';
  if (!isAiAlertTempDashboard && !isAppCompareDashboard && dashboard.unsaved) {
    popMsg = 'Do you want to save this dashboard?';
  } else if (
    !isAiAlertTempDashboard &&
    dashboard.type !== DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS &&
    dashboard.isSaved &&
    dashboard.hasChanged
  ) {
    popMsg = 'Do you want to save the changes?';
  } else {
    showPopConfirm = false;
  }

  if (dashboard.type === DASHBOARD_TYPE.COMPARE) {
    const isLiveModeSelected = get(dashboard, ['config', 'isLiveMode'], false);
    if (isLiveModeSelected) {
      showPopConfirm = false;
    }
  }

  // if(dashboard.type === DASHBOARD_TYPE.EXPERIENCE_ALERT_DIAGNOSTICS) {
  //   showPopConfirm = false;
  // }

  if (customTitle) {
    popMsg = customTitle;
  }

  const defaultLandingPageTab = (defaultLandingPage.url || '').endsWith(url);

  const tabText =
    dashboard.unsaved && dashboard.type !== DASHBOARD_TYPE.COMPARE
      ? combineDashboardName(dashboard.title, dashboard.type) || ''
      : dashboard.title || '';
  const linkContent = () => [
    defaultLandingPageTab ? (
      <Icon
        className={classnames(globalStyles.homeIcon, { [globalStyles.active]: isActive })}
        component={HomeSvg}
        style={{ fontSize: '20px', verticalAlign: '-4.5px' }}
        id="default-icon-temp-dashboard"
      />
    ) : null,
    <span key="tab-text">{tabText.trim()}</span>,
  ];

  const promptForSaveChanges = (e) => {
    e.preventDefault();
    handleSaveActive();
  };

  const ModLink = () => {
    const activeDashboard = dashboards.find((e) => `${e.id}` === `${activeId}`);
    const { pathname } = window.location;
    const defaultPathname = defaultLandingPage.url || '';
    const defaultLandingPageActive = defaultPathname.endsWith(pathname);
    if (!(activeDashboard && activeDashboard.hasChanged && defaultLandingPageActive)) {
      return (
        <Link id={`tab-${dashboard.title}`.toLowerCase()} to={url}>
          {linkContent()}
        </Link>
      );
    }
    return (
      <a id={`tab-${dashboard.title}`.toLowerCase()} onClick={promptForSaveChanges}>
        {linkContent()}
      </a>
    );
  };

  return (
    <li
      className={classnames(
        globalStyles.tab,
        styles.tabWrapper,
        {
          [globalStyles.active]: isActive,
          [styles.active]: isActive,
        },
        className,
      )}
      title={tabText}
    >
      {ModLink()}
      {/* eslint-disable-next-line no-nested-ternary */}
      {defaultLandingPageTab ? null : showPopConfirm ? (
        <Confirm
          title={popMsg}
          onConfirm={handleSave}
          onCancel={handleDontSave}
          okText={okText}
          cancelText={cancelText}
          dashboard={dashboard}
        />
      ) : (
        <Button
          id={`tab-close-${dashboard.title}`.toLowerCase()}
          icon={<CloseOutlined />}
          size="small"
          className={styles.tabCloseBtn}
          onClick={removeTab}
        />
      )}

      <UpdateDashboardPopup
        id={dashboard.unsaved ? -1 : id}
        tempId={!dashboard.unsaved ? -1 : id}
        title={dashboard.title}
        dashboard={dashboard.dashboard || dashboard.config}
        dashboardType={dashboard.type}
      />

      <SaveDashboardPopup
        isPopupOpen={isNewSaveDashboardPopupOpen}
        closePopup={() => setIsNewSaveDashboardPopupOpen(false)}
        pageMeta={{
          page: {
            key: pageKeyForSaveService,
          },
        }}
      />
    </li>
  );
};

DashboardTab.defaultProps = {
  className: '',
  isSaved: false,
  dashboards: [],
  prevDashboard: null,
  toggleUpdatePopupVisible: noop,
  setActiveDashboardId: noop,
  showDefaultLandingPagePrompt: noop,
  setDefaultLandingPagePromptTitle: noop,
  setDefaultLandingPagePromptContent: noop,
  setDefaultLandingPagePromptNeedInput: noop,
  setDefaultLandingPagePromptOkFn: noop,
  setDefaultLandingPage: noop,
  isSharing: false,
  customTitle: '',
  okText: 'Save',
  cancelText: "Don't Save",
  onSave: noop,
  defaultLandingPage: {},
};

export default DashboardTab;
